import { memo, useMemo } from "react";
import type { FieldDef, Node } from "@melo/shared";
import { useTables } from "../lib/store";
import { childrenOf, formatDate, isDone, todayIso } from "../lib/select";
import { Avatars } from "./primitives";
import { StatusChip, fieldText } from "./pickers";
import { LabelChips } from "./labels";
import { Icon } from "./icons";

const PRIORITY_CHIP: Record<string, string> = { urgent: "err", high: "warn" };

/**
 * A board or gallery card, read like a Trello card: the picture of the thing, its labels, its title,
 * and one row of badges. Keys stay off the card; people talk about work by name.
 */
export const Card = memo(function Card({ node, showFields = [], showStatus, selected, onSelect }: { node: Node; showFields?: string[]; showStatus?: boolean; selected?: boolean; onSelect?: (e: React.MouseEvent) => void }) {
  const t = useTables();
  const kind = t.kinds.get(node.kindId);
  const kids = childrenOf(t, node.id);
  const done = kids.filter((k) => isDone(t, k)).length;
  const finished = isDone(t, node);
  const claim = t.claims.get(node.id);
  const priority = typeof node.fields.priority === "string" ? node.fields.priority : null;
  const overdue = node.dueDate && node.dueDate < todayIso() && !finished;
  const cover = node.coverRefId ? t.refs.get(node.coverRefId) : undefined;
  const files = useMemo(() => [...t.refs.values()].filter((r) => r.nodeId === node.id).length, [t.refs, node.id]);
  const checks = node.checklist ?? [];
  const checked = checks.filter((c) => c.done).length;
  const defs = (kind?.fields ?? []).filter((f) => showFields.includes(f.key) && f.key !== "priority");
  const due = node.dueDate ? formatDate(node.dueDate) : null;
  return (
    <div className="card-body">
      {cover?.url && <img className="card-cover" src={cover.url} alt="" loading="lazy" draggable={false} />}
      {onSelect && (
        <button
          type="button"
          className={`card-check${selected ? " on" : ""}`}
          aria-label={selected ? `Deselect ${node.title}` : `Select ${node.title}`}
          aria-pressed={selected}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(e);
          }}
        />
      )}
      {node.labels.length > 0 && <LabelChips ids={node.labels} />}
      <div className={`ct${finished ? " finished" : ""}`}>{node.title}</div>
      {node.blocker && (
        <div className="card-flag">
          <span className="chip sig">{node.blocker.kind === "needs_input" ? "Needs input" : "Blocked"}</span>
        </div>
      )}
      {defs.length > 0 && <CardFields node={node} defs={defs} />}
      {(showStatus || due || (priority && PRIORITY_CHIP[priority]) || kids.length > 0 || checks.length > 0 || files > 0 || claim || node.assignees.some((a) => a.role === "owner")) && (
      <div className="meta">
        {showStatus && <StatusChip kind={kind} status={node.status} />}
        {due && (
          <span className={`badge${finished ? " ok" : overdue ? " late" : ""}`} title={finished ? `Done, was due ${due}` : overdue ? `Overdue since ${due}` : `Due ${due}`}>
            <Icon name="clock" size={12} />
            {due}
          </span>
        )}
        {priority && PRIORITY_CHIP[priority] && <span className={`chip ${PRIORITY_CHIP[priority]}`}>{priority[0]!.toUpperCase() + priority.slice(1)}</span>}
        {kids.length > 0 && (
          <span className={`badge${done === kids.length ? " ok" : ""}`} title={`${done} of ${kids.length} subtasks done`}>
            <Icon name="layers" size={12} />
            {done}/{kids.length}
          </span>
        )}
        {checks.length > 0 && (
          <span className={`badge${checked === checks.length ? " ok" : ""}`} title={`${checked} of ${checks.length} checklist items done`}>
            <Icon name="checkbox" size={12} />
            {checked}/{checks.length}
          </span>
        )}
        {files > 0 && (
          <span className="badge" title={`${files} attachment${files === 1 ? "" : "s"}`}>
            <Icon name="clip" size={12} />
            {files}
          </span>
        )}
        <span className="r">
          {claim && <span className="claim-dot" title={`Being worked on by ${t.actors.get(claim.actorId)?.name ?? "someone"}`} />}
          <Avatars actors={node.assignees.filter((a) => a.role === "owner").map((a) => t.actors.get(a.actorId))} />
        </span>
      </div>
      )}
    </div>
  );
});

function CardFields({ node, defs }: { node: Node; defs: FieldDef[] }) {
  const t = useTables();
  const shown = defs.map((d) => [d, fieldText(d, node.fields[d.key], t.actors)] as const).filter(([, v]) => v);
  if (!shown.length) return null;
  return (
    <div className="card-fields">
      {shown.map(([d, v]) => (
        <span key={d.key}>
          <span className="muted">{d.label}</span> {v}
        </span>
      ))}
    </div>
  );
}
